/* eslint @typescript-eslint/ban-types: 0 */
import { ActionTree, GetterTree, MutationTree } from 'vuex'
import capitalize from 'lodash/capitalize'
import camelCase from 'lodash/camelCase'

interface storeArguments {
  resourceName: string
  // Provide if rule for creating plural is not just adding 's' (i.e. swatch -> swatchES)
  plural?: string
  apiCalls: {
    fetchAllApiCall?: Function
    fetchByIdApiCall?: Function
    createApiCall?: Function
    updateApiCall?: Function
    deleteApiCall?: Function
  }
}

function createBaseStore<ResourceType>({ resourceName, plural, apiCalls }: storeArguments) {
  let resourceNameInPlural = resourceName + 's'
  if (plural) {
    resourceNameInPlural = plural
  }

  interface itemState {
    all: Array<ResourceType>
    singleItem: ResourceType | null
    allError: unknown
    singleItemError: unknown
    allLoading: boolean
    singleItemLoading: boolean
  }

  const state: itemState = {
    all: [],
    allError: null,
    singleItem: null,
    singleItemError: null,
    allLoading: false,
    singleItemLoading: false
  }

  const actions: ActionTree<itemState, unknown> = {}
  if (apiCalls.fetchAllApiCall) {
    actions[`${camelCase(`fetch${capitalize(resourceNameInPlural)}`)}`] = async ({ commit }, query) => {
      try {
        commit(`SET_${resourceNameInPlural.toUpperCase()}_LOADING`, true)
        commit(`SET_${resourceNameInPlural.toUpperCase()}_ERROR`, null)
        const res = apiCalls.fetchAllApiCall ? await apiCalls.fetchAllApiCall(query) : ''
        commit(`SET_${resourceNameInPlural.toUpperCase()}`, res)
      } catch (err) {
        commit(`SET_${resourceNameInPlural.toUpperCase()}_ERROR`, err)
      } finally {
        commit(`SET_${resourceNameInPlural.toUpperCase()}_LOADING`, false)
      }
    }
  }

  if (apiCalls.fetchByIdApiCall) {
    actions[`${camelCase(`fetch${capitalize(resourceName)}`)}`] = async ({ commit }, id) => {
      try {
        commit(`SET_${resourceName.toUpperCase()}_LOADING`, true)
        commit(`SET_${resourceName.toUpperCase()}_ERROR`, null)
        const res = apiCalls.fetchByIdApiCall ? await apiCalls.fetchByIdApiCall(id) : ''
        commit(`SET_${resourceName.toUpperCase()}`, res)
      } catch (err) {
        commit(`SET_${resourceName.toUpperCase()}_ERROR`, err)
      } finally {
        commit(`SET_${resourceName.toUpperCase()}_LOADING`, false)
      }
    }
  }

  if (apiCalls.createApiCall) {
    actions[`create${capitalize(resourceName)}`] = async ({ commit }, payload): Promise<any> => {
      let res
      try {
        commit(`SET_${resourceName.toUpperCase()}_LOADING`, true)
        commit(`SET_${resourceName.toUpperCase()}_ERROR`, null)
        res = apiCalls.createApiCall ? await apiCalls.createApiCall(payload) : ''
        commit(`SET_${resourceName.toUpperCase()}`, res)
      } catch (err) {
        commit(`SET_${resourceName.toUpperCase()}_ERROR`, err)
      } finally {
        commit(`SET_${resourceName.toUpperCase()}_LOADING`, false)
      }
      return res
    }
  }

  if (apiCalls.updateApiCall) {
    actions[`update${capitalize(resourceName)}`] = async ({ commit }, { id, payload }): Promise<any> => {
      let res
      try {
        commit(`SET_${resourceName.toUpperCase()}_LOADING`, true)
        commit(`SET_${resourceName.toUpperCase()}_ERROR`, null)
        res = apiCalls.updateApiCall ? await apiCalls.updateApiCall(id, payload) : ''
        commit(`SET_${resourceName.toUpperCase()}`, res)
      } catch (err) {
        commit(`SET_${resourceName.toUpperCase()}_ERROR`, err)
      } finally {
        commit(`SET_${resourceName.toUpperCase()}_LOADING`, false)
      }
      return res
    }
  }

  if (apiCalls.deleteApiCall) {
    actions[`delete${capitalize(resourceName)}`] = async ({ commit }, payload = null) => {
      try {
        if (payload && apiCalls.deleteApiCall) await apiCalls.deleteApiCall(payload)
        else if (apiCalls.deleteApiCall) await apiCalls.deleteApiCall()
        commit(`SET_${resourceName.toUpperCase()}`, null)
      } catch (err) {
        commit(`SET_${resourceName.toUpperCase()}_ERROR`, err)
      }
    }
  }

  const mutations: MutationTree<itemState> = {
    [`SET_${resourceNameInPlural.toUpperCase()}`](state, items: Array<ResourceType>) {
      state.all = items
    },
    [`SET_${resourceNameInPlural.toUpperCase()}_ERROR`](state, error) {
      state.allError = error
    },
    [`SET_${resourceName.toUpperCase()}`](state, item: ResourceType) {
      state.singleItem = item
    },
    [`SET_${resourceName.toUpperCase()}_ERROR`](state, error) {
      state.singleItemError = error
    },
    [`SET_${resourceName.toUpperCase()}_LOADING`](state, loading) {
      state.singleItemLoading = loading
    },
    [`SET_${resourceNameInPlural.toUpperCase()}_LOADING`](state, loading) {
      state.allLoading = loading
    }
  }

  const getters: GetterTree<itemState, unknown> = {
    [`${resourceNameInPlural}`](state) {
      return state.all
    },

    [`${resourceName}`](state) {
      return state.singleItem
    },

    [`${camelCase(resourceNameInPlural)}Error`](state) {
      return state.allError
    },

    [`${resourceName}Error`](state) {
      return state.singleItemError
    },

    [`${resourceNameInPlural}Loading`](state) {
      return state.allLoading
    }
  }

  return {
    state,
    actions,
    mutations,
    getters,
    namespaced: true
  }
}

export default createBaseStore
